import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatNumber } from '@/lib/utils'
import { Plus, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { apiErrorMessage } from '@/api/client'
import { settings as api } from '@/api/endpoints'
import { Alert, EmptyState, Input, Select, Spinner } from '@/components/ui'
import { useCenters } from '@/features/centers/useCenters'
import type { PricingRow } from '@/types/api'

/**
 * SettingsPresenter::actionPricing.
 *
 * The `pricing` table is a flat list of (persons, hours, language) -> price.
 * An empty language means "foreign language tariff" - see
 * apps/pricing/services.price_for_reservation.
 *
 * Pri viacerých strediskách má každé vlastný cenník (`sql/040`) - prepínač
 * nad tabuľkou a „Kopírovať z…“.
 */
export default function PricingTab() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { active, multi } = useCenters()
  const [centerId, setCenterId] = useState(1)
  const { data, isLoading } = useQuery({
    queryKey: ['pricing', centerId],
    queryFn: () => api.pricing(centerId),
  })

  const [rows, setRows] = useState<PricingRow[]>([])
  const [message, setMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null)

  useEffect(() => {
    if (data) setRows(data.rows)
  }, [data])

  const save = useMutation({
    mutationFn: (payload: PricingRow[]) => api.savePricing(payload, centerId),
    onSuccess: () => {
      setMessage({ kind: 'success', text: t('global.saved') })
      void qc.invalidateQueries({ queryKey: ['pricing'] })
    },
    onError: (err) => setMessage({ kind: 'error', text: apiErrorMessage(err) }),
  })

  const copy = useMutation({
    mutationFn: (fromId: number) => api.copyPricing(fromId, centerId),
    onSuccess: () => {
      setMessage({ kind: 'success', text: t('centers.pricingCopied') })
      void qc.invalidateQueries({ queryKey: ['pricing'] })
    },
    onError: (err) => setMessage({ kind: 'error', text: apiErrorMessage(err) }),
  })

  function update(index: number, patch: Partial<PricingRow>) {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)))
  }

  /** 90 -> "1,5 h" - a readable cross-check next to the minutes input. */
  function formatLength(minutes: number): string {
    return `${formatNumber(minutes / 60)} h`
  }

  if (isLoading) {
    return (
      <div className="flex h-40 items-center justify-center text-slate-400">
        <Spinner className="h-5 w-5" />
      </div>
    )
  }

  return (
    <div className="max-w-3xl space-y-3">
      {multi ? (
        <div className="flex flex-wrap items-end gap-3">
          <label className="block">
            <span className="label">{t('centers.center')}</span>
            <Select
              value={centerId}
              onChange={(e) => {
                setMessage(null)
                setCenterId(Number(e.target.value))
              }}
            >
              {active.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </label>
          <label className="block">
            <span className="label">{t('centers.copyPricingFrom')}</span>
            <Select
              value=""
              disabled={copy.isPending}
              onChange={(e) => {
                const fromId = Number(e.target.value)
                if (fromId && window.confirm(t('centers.copyPricingConfirm'))) copy.mutate(fromId)
              }}
            >
              <option value="">—</option>
              {active
                .filter((c) => c.id !== centerId)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </Select>
          </label>
        </div>
      ) : null}
      <Alert variant="info">{t('settings.pricingHint')}</Alert>
      {message ? <Alert variant={message.kind}>{message.text}</Alert> : null}

      <div className="card overflow-x-auto">
        {rows.length === 0 ? (
          <EmptyState>{t('app.empty')}</EmptyState>
        ) : (
          <table className="table-base">
            <thead>
              <tr>
                <th>{t('reservation.persons')}</th>
                <th>{t('settings.pricingLength')}</th>
                <th>{t('reservation.language')}</th>
                <th className="text-right">{t('reservation.price')}</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {/* Kľúčom je pozícia, nie obsah riadka.

                  Pôvodne bol zložený z hodnôt (`person-minutes-lang-index`),
                  takže sa pri každom stlačení klávesy zmenil. React z toho
                  usúdil, že je to iný riadok — starý odmountoval a nový
                  namountoval. Políčko sa tým stalo novým prvkom v DOM-e a
                  kurzor z neho vypadol; napísať sa dal vždy len jeden znak.

                  Index je tu bezpečný: všetky políčka sú riadené z `rows`,
                  takže si medzi prekresleniami nedržia vlastný stav, ktorý by
                  sa dal pri mazaní riadka pomiešať. */}
              {rows.map((row, index) => (
                <tr key={index}>
                  <td>
                    <Input
                      type="number"
                      min={1}
                      max={20}
                      className="w-20"
                      value={row.person}
                      onChange={(e) =>
                        update(index, { person: Number.parseInt(e.target.value, 10) || 1 })
                      }
                    />
                  </td>
                  <td>
                    <div className="flex items-center gap-1.5">
                      <Input
                        type="number"
                        min={5}
                        max={600}
                        step={5}
                        className="w-24"
                        value={row.minutes}
                        onChange={(e) =>
                          update(index, {
                            minutes: Number.parseInt(e.target.value, 10) || 60,
                          })
                        }
                      />
                      <span className="whitespace-nowrap text-xs text-slate-500">
                        {t('global.minutes')}
                        {row.minutes ? ` · ${formatLength(row.minutes)}` : ''}
                      </span>
                    </div>
                  </td>
                  <td>
                    <Input
                      maxLength={2}
                      className="w-20"
                      placeholder="—"
                      value={row.lang}
                      onChange={(e) => update(index, { lang: e.target.value.toLowerCase() })}
                    />
                  </td>
                  <td>
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      className="w-28 text-right"
                      value={row.price}
                      onChange={(e) => update(index, { price: e.target.value })}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn-ghost p-1 text-red-500"
                      onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          className="btn-secondary"
          onClick={() =>
            setRows((prev) => [...prev, { person: 1, minutes: 60, lang: '', price: '0.00' }])
          }
        >
          <Plus size={15} />
          {t('global.add')}
        </button>
        <button
          type="button"
          className="btn-primary"
          disabled={save.isPending}
          onClick={() => {
            setMessage(null)
            save.mutate(rows)
          }}
        >
          {save.isPending ? <Spinner /> : null}
          {t('global.save')}
        </button>
      </div>
    </div>
  )
}
